/**
 * 运行时 i18n（TECH-BRIEF §5、PRD §8.4、spec-core §1.9）：
 * - t(key, vars)：同步查词条，按 ICU 子集渲染（变量、英语复数、参数变量、I18nText，见 message-format.ts）；
 * - 多命名空间合并加载：只加载当前语言与兜底链上的语言，每种语言加载全部命名空间（默认取加载器声明的全部命名空间）；
 * - 两层词条：内置种子（构建时，兜底）与运行时词条包（`GET /api/v1/i18n/:lang` 返回的 A45 词条库，已由服务端按兜底合并）；
 *   运行时以 A45 为准：A45 有本语言译文时用它，其次本语言种子，再其次 A45 的兜底译文，最后按兜底链查其他语言；
 * - 缺译兜底：用户语言 → 英语 → 中文（`content.i18n_fallback_order` 可调）；显示的不是本语言原文时回调 onMissing（缺译上报）；
 * - 永不显示词条编号：应用层（I18nProvider、createAppI18n）在所有语言都找不到时返回空串（missing: 'empty'）。
 *   createI18n 的默认值 'key' 只为保持骨架行为（D-P0-06-06）。
 * - addMessages：运行时按命名空间覆盖内置种子。
 */
import type { DecimalInput } from '@nexora/shared';
import {
  createFormatter,
  formatDate,
  formatNumber,
  type DateInput,
  type LangFormatter,
} from './format.js';
import { DEFAULT_FALLBACK_ORDER, resolveFallbackChain, type FallbackItem, type Lang } from './langs.js';
import { formatMessage, type MessageValues } from './message-format.js';

export type Messages = Readonly<Record<string, string>>;
/** t() 的变量；值可以是字符串、数字、I18nText（spec-core §4.4）。 */
export type MessageVars = MessageValues;

/** 按语言与命名空间加载词条。 */
export type MessageLoader = (lang: Lang, namespace: string) => Promise<Messages>;

/** 声明了全部命名空间的加载器（内置种子加载器，bundled.ts）。 */
export interface NamespacedLoader extends MessageLoader {
  readonly namespaces: readonly string[];
}

/** 运行时词条包：`GET /api/v1/i18n/:lang?v=` 的响应（spec-core §1.9 bundle）。 */
export interface RemoteBundle {
  /** 词条包版本号（i18n_bundle_state.version） */
  v: number;
  /** key → 文案，已按兜底顺序合并 */
  entries: Messages;
  /** entries 中本语言缺译、取了兜底语言文案的 key（用于缺译上报，D-P0-06-04） */
  missing?: readonly string[];
}

export interface I18nOptions {
  lang: Lang;
  loader: MessageLoader;
  /** 要加载的命名空间；默认取加载器声明的全部命名空间，没有声明时为 ['common'] */
  namespaces?: readonly string[];
  /** 显示的文案不是当前语言原文时回调（无论兜底是否命中），每个 key 每种语言只回调一次 */
  onMissing?: (key: string, lang: Lang) => void;
  /** 缺译兜底顺序（参数 content.i18n_fallback_order），默认用户语言 → en → zh */
  fallbackOrder?: readonly FallbackItem[];
  /** 所有语言都找不到时返回什么：'key'（骨架行为）或 'empty'（永不显示词条编号） */
  missing?: 'key' | 'empty';
  /** `{param:<key>}` 的当前值文本（用户端可见参数）；不提供时显示为空 */
  param?: (key: string) => string | null | undefined;
}

export interface I18n {
  readonly lang: Lang;
  readonly namespaces: readonly string[];
  /** 词条（种子、覆盖、运行时词条包）每变化一次加 1，供界面重新渲染 */
  readonly revision: number;
  /** 加载 lang（默认当前语言）兜底链上的全部词条 */
  load(lang?: Lang): Promise<void>;
  /** 切换语言：先加载，再切换并通知订阅者 */
  setLang(lang: Lang): Promise<void>;
  t(key: string, vars?: MessageVars): string;
  has(key: string, lang?: Lang): boolean;
  isLoaded(lang?: Lang): boolean;
  addMessages(lang: Lang, namespace: string, messages: Messages): void;
  /** 写入运行时词条包；版本号小于已有版本的包被忽略 */
  setRemote(lang: Lang, bundle: RemoteBundle): boolean;
  /** 某语言已写入的运行时词条包版本号；没有时为 null */
  remoteVersion(lang?: Lang): number | null;
  /** 更新兜底顺序（参数变化时） */
  setFallbackOrder(order: readonly FallbackItem[]): void;
  formatNumber(value: DecimalInput, places: number): string;
  formatDate(date: DateInput, options?: Intl.DateTimeFormatOptions): string;
  /** 当前语言的固定日期 / 时间写法（PRD 5.14 A45「格式」） */
  readonly format: LangFormatter;
  /** 语言切换时回调 */
  subscribe(listener: (lang: Lang) => void): () => void;
  /** 语言或词条变化时回调 */
  onChange(listener: () => void): () => void;
}

const PLACEHOLDER = /\{(\w+)\}/g;
/** I18nText 嵌套渲染的最大深度，防止词条互相引用造成死循环 */
const MAX_TEXT_DEPTH = 4;

/** 用 vars 替换 {name} 占位；没有提供的占位原样保留（骨架工具函数；词条渲染用 formatMessage）。 */
export function interpolate(template: string, vars?: Readonly<Record<string, string | number>>): string {
  if (!vars) return template;
  return template.replace(PLACEHOLDER, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

interface RemoteLayer {
  v: number;
  entries: Map<string, string>;
  missing: Set<string>;
}

type Lookup = { value: string; native: boolean } | undefined;

export function createI18n(options: I18nOptions): I18n {
  const declared = (options.loader as Partial<NamespacedLoader>).namespaces;
  const namespaces =
    options.namespaces && options.namespaces.length > 0
      ? [...options.namespaces]
      : declared && declared.length > 0
        ? [...declared]
        : ['common'];
  const store = new Map<string, Map<string, string>>(); // `${lang}:${ns}` → messages
  const remote = new Map<Lang, RemoteLayer>();
  const pending = new Map<string, Promise<void>>();
  const listeners = new Set<(lang: Lang) => void>();
  const changeListeners = new Set<() => void>();
  const reported = new Set<string>();
  const missingMode = options.missing ?? 'key';
  let fallbackOrder: readonly FallbackItem[] = options.fallbackOrder ?? DEFAULT_FALLBACK_ORDER;
  let current: Lang = options.lang;
  let revision = 0;
  let formatter = createFormatter(current);

  const slot = (lang: Lang, ns: string): string => `${lang}:${ns}`;
  const chainOf = (lang: Lang): Lang[] => resolveFallbackChain(lang, fallbackOrder);

  function changed(): void {
    revision += 1;
    for (const listener of changeListeners) listener();
  }

  function loadOne(lang: Lang, ns: string): Promise<void> {
    const id = slot(lang, ns);
    if (store.has(id)) return Promise.resolve();
    let task = pending.get(id);
    if (!task) {
      task = options
        .loader(lang, ns)
        .then((messages) => {
          const existing = store.get(id);
          const merged = new Map(Object.entries(messages));
          // 加载期间若已有 addMessages 覆盖，以覆盖为准
          if (existing) for (const [k, v] of existing) merged.set(k, v);
          store.set(id, merged);
          changed();
        })
        .finally(() => pending.delete(id));
      pending.set(id, task);
    }
    return task;
  }

  function seedLookup(lang: Lang, key: string): string | undefined {
    for (const ns of namespaces) {
      const value = store.get(slot(lang, ns))?.get(key);
      if (value !== undefined) return value;
    }
    return undefined;
  }

  /** 按 A45 优先的顺序查一个 key（见文件头）。native = 显示的是 lang 本语言原文。 */
  function lookup(lang: Lang, key: string): Lookup {
    const layer = remote.get(lang);
    const remoteValue = layer?.entries.get(key);
    if (remoteValue !== undefined && !layer!.missing.has(key)) return { value: remoteValue, native: true };
    const seed = seedLookup(lang, key);
    if (seed !== undefined) return { value: seed, native: remoteValue === undefined };
    if (remoteValue !== undefined) return { value: remoteValue, native: false };
    const chain = chainOf(lang);
    for (let i = 1; i < chain.length; i += 1) {
      const other = chain[i] as Lang;
      const otherLayer = remote.get(other);
      const otherRemote = otherLayer?.entries.get(key);
      if (otherRemote !== undefined && !otherLayer!.missing.has(key)) return { value: otherRemote, native: false };
      const otherSeed = seedLookup(other, key);
      if (otherSeed !== undefined) return { value: otherSeed, native: false };
    }
    return undefined;
  }

  function render(key: string, vars: MessageVars | undefined, depth: number): string {
    const found = lookup(current, key);
    if (!found || !found.native) reportMissing(key, current);
    if (!found) return missingMode === 'key' && depth === 0 ? key : '';
    return formatMessage(found.value, {
      lang: current,
      vars,
      param: options.param,
      text: (nestedKey, nestedVars) => (depth >= MAX_TEXT_DEPTH ? '' : render(nestedKey, nestedVars, depth + 1)),
    });
  }

  const i18n: I18n = {
    get lang() {
      return current;
    },
    namespaces,

    get revision() {
      return revision;
    },

    get format() {
      return formatter;
    },

    async load(lang = current) {
      await Promise.all(chainOf(lang).flatMap((l) => namespaces.map((ns) => loadOne(l, ns))));
    },

    async setLang(lang) {
      await i18n.load(lang);
      if (lang === current) return;
      current = lang;
      formatter = createFormatter(lang);
      for (const listener of listeners) listener(lang);
      changed();
    },

    t(key, vars) {
      return render(key, vars, 0);
    },

    has(key, lang = current) {
      return seedLookup(lang, key) !== undefined || remote.get(lang)?.entries.has(key) === true;
    },

    isLoaded(lang = current) {
      return chainOf(lang).every((l) => namespaces.every((ns) => store.has(slot(l, ns))));
    },

    addMessages(lang, namespace, messages) {
      const id = slot(lang, namespace);
      const target = store.get(id) ?? new Map<string, string>();
      for (const [k, v] of Object.entries(messages)) target.set(k, v);
      store.set(id, target);
      changed();
    },

    setRemote(lang, bundle) {
      const existing = remote.get(lang);
      if (existing && bundle.v < existing.v) return false;
      remote.set(lang, {
        v: bundle.v,
        entries: new Map(Object.entries(bundle.entries)),
        missing: new Set(bundle.missing ?? []),
      });
      // 新的词条包可能补上了译文：允许重新上报仍然缺译的 key
      for (const id of [...reported]) if (id.startsWith(`${lang}:`)) reported.delete(id);
      changed();
      return true;
    },

    remoteVersion(lang = current) {
      return remote.get(lang)?.v ?? null;
    },

    setFallbackOrder(order) {
      fallbackOrder = order;
      changed();
    },

    formatNumber(value, places) {
      return formatNumber(current, value, places);
    },

    formatDate(date, formatOptions) {
      return formatDate(current, date, formatOptions);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    onChange(listener) {
      changeListeners.add(listener);
      return () => changeListeners.delete(listener);
    },
  };

  function reportMissing(key: string, lang: Lang): void {
    if (!options.onMissing) return;
    const id = `${lang}:${key}`;
    if (reported.has(id)) return;
    reported.add(id);
    options.onMissing(key, lang);
  }

  return i18n;
}
